import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileCheck2, Loader2, ShieldCheck, Save, IdCard } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import SignaturePad from "./SignaturePad";

/**
 * DriverVerificationCard (v4.1.0) — customer-facing Digital Driver
 * Verification: drag-and-drop ID + licence upload with OCR review, plus a
 * touch e-signature that signs the rental contract. Once both documents and
 * the signature are in, the backend stamps `verified_at` and the Digital
 * Pass badge flips to "Ready to Drive".
 */
export default function DriverVerificationCard({ bookingId, verification, onUpdate }) {
  const dv = verification || {};
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState("");

  const upload = async (docType, file) => {
    if (!file) return;
    setBusy(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const { data } = await api.post(`/bookings/${bookingId}/ocr-verify`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdate?.(data.driver_verification);
      toast.success(docType === "id_card" ? "ID uploaded & scanned" : "Licence uploaded & scanned");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const sign = async () => {
    if (!signature) { toast.warning("Please draw your signature first."); return; }
    setBusy("sign");
    try {
      const { data } = await api.post(`/bookings/${bookingId}/sign`, { signature_data_url: signature });
      onUpdate?.(data.driver_verification);
      toast.success("Contract signed");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const ocr = dv.ocr_extracted_data || {};

  return (
    <div className="bg-white border border-black/10 rounded-xl p-7 shadow-sm" data-testid="mb-driver-verification">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm uppercase tracking-[0.2em] text-neutral-700 font-semibold inline-flex items-center gap-2">
          <IdCard size={16} /> Digital driver verification
        </h2>
        <span
          className={`text-xs font-semibold px-3 py-1 rounded-full ${
            dv.verified_at ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
          }`}
          data-testid="mb-verification-status"
        >
          {dv.verified_at ? "Verified · Ready to Drive" : "Pending verification"}
        </span>
      </div>
      <p className="text-sm text-neutral-500 mt-2">
        Upload your ID and driving licence, then sign the rental agreement to skip the desk paperwork.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        <DocDrop
          label="Identity card / Passport"
          testId="upload-id-card"
          url={dv.id_card_url}
          busy={busy === "id_card"}
          onFile={(f) => upload("id_card", f)}
        />
        <DocDrop
          label="Driving licence"
          testId="upload-driver-license"
          url={dv.driver_license_url}
          busy={busy === "driver_license"}
          onFile={(f) => upload("driver_license", f)}
        />
      </div>

      {(ocr.doc_number || ocr.full_name) && (
        <div className="mt-5 bg-neutral-50 border border-black/8 rounded-lg p-4" data-testid="mb-ocr-review">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Scanned details — please review</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
            <OcrField label="Full name" value={ocr.full_name} />
            <OcrField label="Document no." value={ocr.doc_number} />
            <OcrField label="Expiry" value={ocr.expiry_date} />
            <OcrField label="Date of birth" value={ocr.birth_date} />
          </div>
        </div>
      )}

      <div className="mt-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Rental agreement signature</p>
        {dv.signature_data_url ? (
          <div className="flex items-center gap-4 flex-wrap" data-testid="mb-signature-saved">
            <img
              src={dv.signature_data_url}
              alt="Signature"
              className="h-20 bg-white border border-black/10 rounded-lg px-3"
            />
            <p className="text-sm text-emerald-700 inline-flex items-center gap-1.5">
              <ShieldCheck size={15} /> Signed{dv.signed_at ? ` · ${String(dv.signed_at).slice(0, 10)}` : ""}
            </p>
          </div>
        ) : (
          <>
            <SignaturePad onChange={setSignature} />
            <button
              type="button"
              onClick={sign}
              disabled={busy === "sign"}
              className="mt-3 inline-flex items-center gap-2 bg-black text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50"
              data-testid="mb-sign-contract-btn"
            >
              {busy === "sign" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Save &amp; Sign Contract
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function OcrField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="text-neutral-800 mt-0.5 break-words">{value || "—"}</p>
    </div>
  );
}

function DocDrop({ label, url, busy, onFile, testId }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onFile(e.dataTransfer.files?.[0]);
      }}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer border border-dashed rounded-xl p-4 text-center transition-colors ${
        over ? "border-black bg-neutral-50" : url ? "border-emerald-300 bg-emerald-50/50" : "border-neutral-300"
      }`}
      data-testid={testId}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      {busy ? (
        <Loader2 size={20} className="mx-auto animate-spin text-neutral-500" />
      ) : url ? (
        <FileCheck2 size={20} className="mx-auto text-emerald-600" />
      ) : (
        <UploadCloud size={20} className="mx-auto text-neutral-400" />
      )}
      <p className="text-sm font-medium text-neutral-800 mt-2">{label}</p>
      <p className="text-xs text-neutral-500 mt-0.5">
        {url ? "Uploaded — click to replace" : "Drag & drop or click · PNG, JPG, PDF"}
      </p>
    </div>
  );
}
